import { Badge, Card, Empty, Table, Td, Th } from "../../../components/ui";
import { LoadMore } from "../../../components/LoadMore";
import { ResourceView } from "../../../components/states";
import type { AppConfig } from "../../../config";
import { labeebPage, usePagedList } from "../../../hooks/usePagedList";
import {
  labeebPaged,
  labeebTenantUsersPath,
  labeebUserLabel,
  type LabeebUser,
} from "../../../api/labeeb";
import { LabeebUserBlock } from "../../../components/labeeb/LabeebUserBlock";
import { LabeebMissing } from "./labeebMissing";

/**
 * The people provisioned in this tenant, owner first.
 *
 * `PATCH …/users/{user_id}/standing` blocks one person. There is deliberately
 * no fleet-wide equivalent — a list spraying every merchant's staff names and
 * emails is a different privacy posture from looking up the tenant you are
 * already supporting.
 */
export function LabeebUsersPanel({ config, tenantId }: { config: AppConfig; tenantId: string }) {
  const users = usePagedList<LabeebUser>(
    config,
    labeebPaged(labeebTenantUsersPath(tenantId)),
    labeebPage,
  );
  const state = users.state;

  return (
    <Card title="Users">
      <ResourceView
        state={state}
        what="users"
        onRetry={users.reload}
        notFound={<LabeebMissing state={state} />}
      />

      {state.kind === "ready" &&
        (state.data.rows.length === 0 ? (
          <>
            <Empty>Nobody is provisioned in this tenant yet.</Empty>
            <LoadMore
              shown={0}
              noun="people"
              hasMore={state.data.next !== null}
              loading={users.loadingMore}
              error={users.moreError}
              onMore={users.loadMore}
            />
          </>
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>Person</Th>
                  <Th>Role</Th>
                  <Th>Standing</Th>
                  <Th>Last seen</Th>
                </tr>
              }
            >
              {state.data.rows.map((u) => (
                <tr key={u.user_id}>
                  <Td>
                    {/* name/email can still be absent on a row Labeeb has
                        seen but never resolved — fall back rather than blank. */}
                    <span className="ref">{labeebUserLabel(u)}</span>
                    {u.email && u.name && (
                      <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                        {u.email}
                      </span>
                    )}
                  </Td>
                  <Td>{u.is_owner && <Badge tone="neutral">owner</Badge>}</Td>
                  <Td>
                    {/* REAL ENFORCEMENT, unlike the old `is_active` label.
                        A blocked user's next request is refused. */}
                    {u.status === "blocked" ? (
                      <div>
                        <Badge tone="red">blocked</Badge>
                        {(u.blocked_by || u.reason) && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {u.blocked_by ? `by ${u.blocked_by}` : ""}
                            {u.reason ? ` — "${u.reason}"` : ""}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {u.status ?? "—"}
                      </span>
                    )}
                    <LabeebUserBlock
                      config={config}
                      tenantId={tenantId}
                      user={u}
                      onChanged={users.reload}
                    />
                  </Td>
                  <Td>
                    {/* `last_seen` is written by the REQUEST path, so it means
                        what its name says. The old `last_active_at` moved only
                        on chat create or delete — never on sending a message —
                        and was withheld for that reason. */}
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {u.last_seen ? new Date(u.last_seen).toLocaleDateString() : "—"}
                    </span>
                  </Td>
                </tr>
              ))}
            </Table>

            <LoadMore
              shown={state.data.rows.length}
              noun="people"
              hasMore={state.data.next !== null}
              loading={users.loadingMore}
              error={users.moreError}
              onMore={users.loadMore}
            />
          </>
        ))}
    </Card>
  );
}
